import type { AxiosError } from 'axios';

export type ValidatorErrors = Record<string, string[]>;

export type Validator = {
	error_count: number;
	errors: ValidatorErrors;
};

export type ApiErrorData = {
	success: boolean;
	status: number;
	class: string;
	validator?: Validator;
	message: string;
	errorCode?: ErrorCodes;
};

export type RTKQueryError = {
	status: number;
	data: ApiErrorData;
};

export type AxiosErrorResponse = AxiosError<ApiErrorData>;

export enum ErrorCodes {
	ACCOUNT_DETAILS_EXCEPTION = 'ACCOUNT_DETAILS_EXCEPTION',
	ACCOUNT_DETAILS_NOT_FOUND = 'ACCOUNT_DETAILS_NOT_FOUND',
	ACCOUNT_DETAILS_UNKNOWN_ERROR = 'ACCOUNT_DETAILS_UNKNOWN_ERROR',
	ADD_RECORDING_CONFLICTS = 'ADD_RECORDING_CONFLICTS',
	ADD_RECORDING_DISK_FULL = 'ADD_RECORDING_DISK_FULL',
	ADD_RECORDING_FAILURE_EXCEPTION = 'ADD_RECORDING_FAILURE_EXCEPTION',
	ADD_RECORDING_UNKNOWN_ERROR = 'ADD_RECORDING_UNKNOWN_ERROR',
	APPSTORE_INVALID_RESULT_CODE_EXCEPTION = 'APPSTORE_INVALID_RESULT_CODE_EXCEPTION',
	APPSTORE_MISSING_RESPONSE_DATA = 'APPSTORE_MISSING_RESPONSE_DATA',
	APP_CHECK_TOKEN_REQUIRED = 'APP_CHECK_TOKEN_REQUIRED',
	AUTHENTICATION_FAILED = 'AUTHENTICATION_FAILED',
	AUTHENTICATION_REQUIRED = 'AUTHENTICATION_REQUIRED',
	BEARER_TOKEN_REQUIRED = 'BEARER_TOKEN_REQUIRED',
	BLOCKED_BY_PARENTAL_CONTROL = 'BLOCKED_BY_PARENTAL_CONTROL',
	CANNOT_BUY_PRODUCT = 'CANNOT_BUY_PRODUCT',
	CANNOT_DETERMINE_SYSTEM = 'CANNOT_DETERMINE_SYSTEM',
	CANNOT_OVERWRITE_SUBSCRIBER_ID = 'CANNOT_OVERWRITE_SUBSCRIBER_ID',
	CANNOT_USE_PROMOTION = 'CANNOT_USE_PROMOTION',
	CATCHUP_NOT_ACTIVE_FOR_CHANNEL = 'CATCHUP_NOT_ACTIVE_FOR_CHANNEL',
	CATCHUP_NOT_AVAILABLE_FOR_PROGRAM = 'CATCHUP_NOT_AVAILABLE_FOR_PROGRAM',
	COUNTRY_ACCESS_DENIED = 'COUNTRY_ACCESS_DENIED',
	DELETE_RECORDING_EXCEPTION = 'DELETE_RECORDING_EXCEPTION',
	DELETE_RECORDING_NOT_FOUND = 'DELETE_RECORDING_NOT_FOUND',
	DELETE_RECORDING_UNKNOWN_ERROR = 'DELETE_RECORDING_UNKNOWN_ERROR',
	DELETE_RECORDING_WRONG_PIN = 'DELETE_RECORDING_WRONG_PIN',
	DEVICE_AUTHENTICATION_FAILED = 'DEVICE_AUTHENTICATION_FAILED',
	DEVICE_CHANGES_LIMIT_EXCEEDED_EXCEPTION = 'DEVICE_CHANGES_LIMIT_EXCEEDED_EXCEPTION',
	DEVICE_LIMIT_EXCEEDED_EXCEPTION = 'DEVICE_LIMIT_EXCEEDED_EXCEPTION',
	DEVICE_NOT_EXISTS = 'DEVICE_NOT_EXISTS',
	DEVICES_LIMIT_EXCEEDED = 'DEVICES_LIMIT_EXCEEDED',
	ERROR_WHILE_SAVING_PORTS_FILE = 'ERROR_WHILE_SAVING_PORTS_FILE',
	ERROR_WHILE_SAVING_TOPOLOGY_FILE = 'ERROR_WHILE_SAVING_TOPOLOGY_FILE',
	ESALES_ADDRESS_NOT_FOUND_EXCEPTION = 'ESALES_ADDRESS_NOT_FOUND_EXCEPTION',
	ESALES_AUTHENTICATION_FAILED_EXCEPTION = 'ESALES_AUTHENTICATION_FAILED_EXCEPTION',
	ESALES_CUSTOMER_NOT_FOUND_EXCEPTION = 'ESALES_CUSTOMER_NOT_FOUND_EXCEPTION',
	ESALES_DEVICE_NOT_FOUND = 'ESALES_DEVICE_NOT_FOUND',
	ESALES_EMAIL_NOT_FOUND_EXCEPTION = 'ESALES_EMAIL_NOT_FOUND_EXCEPTION',
	ESALES_OFFER_NOT_AVAILABLE = 'ESALES_OFFER_NOT_AVAILABLE',
	ESALES_SERVICE_UNAVAILABLE = 'ESALES_SERVICE_UNAVAILABLE',
	GOOGLE_RECAPTCHA_INVALID_TOKEN = 'GOOGLE_RECAPTCHA_INVALID_TOKEN',
	INVALID_ACCESS_KEY = 'INVALID_ACCESS_KEY',
	INVALID_API_DEVICE_HEADER = 'INVALID_API_DEVICE_HEADER',
	INVALID_ARGUMENT = 'INVALID_ARGUMENT',
	INVALID_ORDER_ID_EXCEPTION = 'INVALID_ORDER_ID_EXCEPTION',
	INVALID_PACKET_EXCEPTION = 'INVALID_PACKET_EXCEPTION',
	INVALID_PARENTAL_CONTROL_PIN = 'INVALID_PARENTAL_CONTROL_PIN',
	INVALID_PAYMENT_PIN = 'INVALID_PAYMENT_PIN',
	INVALID_PIN = 'INVALID_PIN',
	INVALID_PLATFORM = 'INVALID_PLATFORM',
	INVALID_PRICE_ENTRY = 'INVALID_PRICE_ENTRY',
	INVALID_PRODUCT_TYPE = 'INVALID_PRODUCT_TYPE',
	INVALID_PROFILE_ID_EXCEPTION = 'INVALID_PROFILE_ID_EXCEPTION',
	INVALID_PROMOTION_CODE = 'INVALID_PROMOTION_CODE',
	INVALID_SERIAL_NUMBER = 'INVALID_SERIAL_NUMBER',
	INVALID_SUBSCRIBER_LOGIN_CODE_EXCEPTION = 'INVALID_SUBSCRIBER_LOGIN_CODE_EXCEPTION',
	INVALID_SUBSCRIBER_SOURCE_EXCEPTION = 'INVALID_SUBSCRIBER_SOURCE_EXCEPTION',
	INVALID_UUID = 'INVALID_UUID',
	IP_BLACKLISTED = 'IP_BLACKLISTED',
	LIST_RECORDING_FAILURE_EXCEPTION = 'LIST_RECORDING_FAILURE_EXCEPTION',
	MAPPING_FILE_NOT_FOUND = 'MAPPING_FILE_NOT_FOUND',
	MISSING_EMAIL = 'MISSING_EMAIL',
	MISSING_EXTERNAL_ID_FOR_PRICE = 'MISSING_EXTERNAL_ID_FOR_PRICE',
	MODEL_NOT_FOUND = 'MODEL_NOT_FOUND',
	MULTIPLE_ACCOUNT_AVAILABLE = 'MULTIPLE_ACCOUNT_AVAILABLE',
	MUST_BE_IN_LOCAL = 'MUST_BE_IN_LOCAL',
	MYSQL_TIMEOUT_INVALID_CASE = 'MYSQL_TIMEOUT_INVALID_CASE',
	NDVR_CONNECTION_REFUSED = 'NDVR_CONNECTION_REFUSED',
	NOSERVICE_DEVICE = 'NOSERVICE_DEVICE',
	NOT_AVAILABLE_ON_PLATFORM = 'NOT_AVAILABLE_ON_PLATFORM',
	NOT_ENOUGH_CREDITS = 'NOT_ENOUGH_CREDITS',
	NO_ACCESS_TO_SYSTEM_EXCEPTION = 'NO_ACCESS_TO_SYSTEM_EXCEPTION',
	NPVR_NOT_ACTIVE_FOR_CHANNEL = 'NPVR_NOT_ACTIVE_FOR_CHANNEL',
	NPVR_NOT_AVAILABLE_FOR_PROGRAM = 'NPVR_NOT_AVAILABLE_FOR_PROGRAM',
	ONE_TIME_CODE_IS_ASSIGNED_TO_ANOTHER_PACKET = 'ONE_TIME_CODE_IS_ASSIGNED_TO_ANOTHER_PACKET',
	ONE_TIME_CODE_PROMOTION_NOT_AVAILABLE_FOR_USER = 'ONE_TIME_CODE_PROMOTION_NOT_AVAILABLE_FOR_USER',
	ONE_TIME_CODE_IS_EXPIRED = 'ONE_TIME_CODE_IS_EXPIRED',
	ONE_TIME_CODE_IS_USED = 'ONE_TIME_CODE_IS_USED',
	ONE_TIME_CODE_NOT_EXIST = 'ONE_TIME_CODE_NOT_EXIST',
	OPERATORS_PASSWORD_RESET_LINK_EXPIRED_EXCEPTION = 'OPERATORS_PASSWORD_RESET_LINK_EXPIRED_EXCEPTION',
	OUTSIDE_OF_EOG = 'OUTSIDE_OF_EOG',
	OUTSIDE_OF_POLAND = 'OUTSIDE_OF_POLAND',
	PACKETS_SUBSCRIPTION_ALREADY_EXIST = 'PACKETS_SUBSCRIPTION_ALREADY_EXIST',
	PADI_TOKEN_SERVICE_UNAVAILABLE = 'PADI_TOKEN_SERVICE_UNAVAILABLE',
	PARTNER_ERROR = 'PARTNER_ERROR',
	PAYMENT_ALREADY_EXISTS = 'PAYMENT_ALREADY_EXISTS',
	PAYMENT_ERROR = 'PAYMENT_ERROR',
	PAYPAL_SERVICE_CONNECTION_LOST = 'PAYPAL_SERVICE_CONNECTION_LOST',
	PAY_PAL_CANCEL_SUBSCRIPTION_EXCEPTION = 'PAY_PAL_CANCEL_SUBSCRIPTION_EXCEPTION',
	PIN_CANNOT_BE_EMPTY = 'PIN_CANNOT_BE_EMPTY',
	PIN_WRONG_OLD_PIN = 'PIN_WRONG_OLD_PIN',
	PLATFORM_MISSING = 'PLATFORM_MISSING',
	PORTS_SCHEMA_FILE_NOT_FOUND = 'PORTS_SCHEMA_FILE_NOT_FOUND',
	PRICE_ENTRY_OUT_OF_DATE = 'PRICE_ENTRY_OUT_OF_DATE',
	PROGRAM_BLACKOUT = 'PROGRAM_BLACKOUT',
	PROGRAM_NOT_FOUND = 'PROGRAM_NOT_FOUND',
	PUREOTT_SUBSCRIBER_ACCOUNT_SUSPENDED = 'PUREOTT_SUBSCRIBER_ACCOUNT_SUSPENDED',
	PUREOTT_SUBSCRIBER_EMAIL_APPROVE_EXPIRED = 'PUREOTT_SUBSCRIBER_EMAIL_APPROVE_EXPIRED',
	PUREOTT_SUBSCRIBER_EMAIL_NOT_CONFIRMED = 'PUREOTT_SUBSCRIBER_EMAIL_NOT_CONFIRMED',
	PUREOTT_SUBSCRIBER_NOT_FOUND = 'PUREOTT_SUBSCRIBER_NOT_FOUND',
	PUREOTT_SUBSCRIBER_WRONG_CREDENTIALS = 'PUREOTT_SUBSCRIBER_WRONG_CREDENTIALS',
	RECEIPT_SUBSCRIPTION_ALREADY_EXISTS = 'RECEIPT_SUBSCRIPTION_ALREADY_EXISTS',
	RECEIPT_SUBSCRIPTION_EXISTS_FOR_ANOTHER_USER = 'RECEIPT_SUBSCRIPTION_EXISTS_FOR_ANOTHER_USER',
	RECORDING_MAX_TIME_AVAILABLE_EXCEEDED_EXCEPTION = 'RECORDING_MAX_TIME_AVAILABLE_EXCEEDED_EXCEPTION',
	RESOURCE_ALREADY_EXISTS = 'RESOURCE_ALREADY_EXISTS',
	RESOURCE_NOT_ACTIVE = 'RESOURCE_NOT_ACTIVE',
	RESOURCE_NOT_AVAILABLE = 'RESOURCE_NOT_AVAILABLE',
	RESOURCE_NOT_FOUND = 'RESOURCE_NOT_FOUND',
	RESOURCE_NOT_IN_SUBSCRIBER_PRODUCTS = 'RESOURCE_NOT_IN_SUBSCRIBER_PRODUCTS',
	SEND_INVOICE_API_ERROR = 'SEND_INVOICE_API_ERROR',
	SGT_AUTHENTICATION_FAILED = 'SGT_AUTHENTICATION_FAILED',
	STARTOVER_NOT_ACTIVE_FOR_CHANNEL = 'STARTOVER_NOT_ACTIVE_FOR_CHANNEL',
	STB_CONNECTION_REFUSED = 'STB_CONNECTION_REFUSED',
	SUBSCRIBER_ASSIGNED_DEVICES_LIMIT_EXCEEDED = 'SUBSCRIBER_ASSIGNED_DEVICES_LIMIT_EXCEEDED',
	SUBSCRIBER_BLOCKED = 'SUBSCRIBER_BLOCKED',
	SUBSCRIBER_DEVICES_CHANGING_LIMIT_EXCEEDED = 'SUBSCRIBER_DEVICES_CHANGING_LIMIT_EXCEEDED',
	SUBSCRIBER_EXTERNAL_PROVIDER_RECORD_NOT_FOUND = 'SUBSCRIBER_EXTERNAL_PROVIDER_RECORD_NOT_FOUND',
	SUBSCRIBER_GROUP_STREAMS_LIMIT_EXCEEDED = 'SUBSCRIBER_GROUP_STREAMS_LIMIT_EXCEEDED',
	SUBSCRIBER_LOGIN_CODE_EXPIRED = 'SUBSCRIBER_LOGIN_CODE_EXPIRED',
	SUBSCRIBER_NOT_REGISTERED_AND_NOT_LINKED = 'SUBSCRIBER_NOT_REGISTERED_AND_NOT_LINKED',
	SUBSCRIBER_PARALLEL_STREAMS_LIMIT_EXCEEDED = 'SUBSCRIBER_PARALLEL_STREAMS_LIMIT_EXCEEDED',
	SUBSCRIBER_SUSPENDED_EXCEPTION = 'SUBSCRIBER_SUSPENDED_EXCEPTION',
	TOPOLOGY_SCHEMA_FILE_NOT_FOUND = 'TOPOLOGY_SCHEMA_FILE_NOT_FOUND',
	UNKNOWN_DEVICE = 'UNKNOWN_DEVICE',
	UNPROVISIONED_DEVICE = 'UNPROVISIONED_DEVICE',
	VIDEO_NOT_FOUND = 'VIDEO_NOT_FOUND',
	VIDEO_SESSION_INVALID = 'VIDEO_SESSION_INVALID'
}
